import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { ArrowLeft } from "lucide-react";
import { SkillTabs } from "@/components/learn/dbt/SkillTabs";
import { CompletionCard } from "@/components/learn/dbt/CompletionCard";
import { FavoriteButton } from "@/components/FavoriteButton";
import { useDbtProgress } from "@/hooks/useDbtProgress";

export const Route = createFileRoute("/learn/dbt/$module/$skill")({
  loader: async ({ params }) => {
    const { findSkill } = await import("@/data/learn/dbt");
    const found = findSkill(params.module, params.skill);
    if (!found) throw notFound();
    return found;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Skill not found — DBT" }, { name: "robots", content: "noindex" }] };
    }
    const title = `${loaderData.skill.title} — ${loaderData.module.label}`;
    const url = `https://recoverytrauma.com/learn/dbt/${params.module}/${params.skill}`;
    return {
      meta: [
        { title: `${title} · Recovery Trauma™` },
        { name: "description", content: loaderData.skill.summary },
        { property: "og:title", content: title },
        { property: "og:description", content: loaderData.skill.summary },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...gatedRobotsMeta(`/learn/dbt/${params.module}/${params.skill}`),
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: SkillPage,
  notFoundComponent: SkillNotFound,
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <p className="text-sm text-rose-600">Something went wrong loading this skill.</p>
      <p className="mt-2 text-xs text-muted-foreground">
        {error instanceof Error ? error.message : "Please try again."}
      </p>
      <Link to="/learn/dbt" className="mt-6 inline-block text-sm text-gold underline">Back to DBT</Link>
    </main>
  ),
});

function SkillNotFound() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="font-serif text-3xl text-foreground">Skill not found</h1>
      <p className="mt-3 text-sm text-muted-foreground">This DBT skill doesn't exist yet.</p>
      <Link to="/learn/dbt" className="mt-6 inline-block text-sm text-gold underline">All DBT modules</Link>
    </main>
  );
}

const THEME_BG: Record<string, string> = {
  sage: "bg-gradient-to-b from-emerald-50/60 via-background to-background",
  sky: "bg-gradient-to-b from-sky-50/60 via-background to-background",
  peach: "bg-gradient-to-b from-rose-50/60 via-background to-background",
  lavender: "bg-gradient-to-b from-violet-50/60 via-background to-background",
  gold: "bg-gradient-to-b from-amber-50/60 via-background to-background",
};

function SkillPage() {
  const { module, skill } = Route.useLoaderData();
  const { isCompleted } = useDbtProgress();
  const done = isCompleted(module.id, skill.id);

  return (
    <main className={`min-h-dvh px-4 py-10 ${THEME_BG[module.theme] ?? ""}`}>
      <div className="mx-auto max-w-3xl">
        <Link
          to="/learn/dbt/$module"
          params={{ module: module.id }}
          className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> {module.emoji} {module.label}
        </Link>

        <header className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                {module.emoji} {module.label}
              </span>
              <h1 className="mt-3 font-serif text-3xl tracking-tight text-foreground md:text-4xl">
                {skill.emoji} {skill.title}
              </h1>
              <p className="mt-2 text-base leading-relaxed text-foreground/80">{skill.summary}</p>
            </div>
            <FavoriteButton
              storageKey="rt:fav-dbt"
              id={`${module.id}/${skill.id}`}
              label={skill.title}
              sub={module.label}
              to={`/learn/dbt/${module.id}/${skill.id}`}
              size="md"
            />
          </div>
        </header>

        <SkillTabs moduleId={module.id} skill={skill} />

        {done && (
          <div className="mt-10">
            <CompletionCard moduleId={module.id} skillId={skill.id} />
          </div>
        )}

        <p className="mt-10 rounded-2xl border border-border bg-muted/30 p-4 text-center text-xs leading-relaxed text-muted-foreground">
          Educational only. For urgent support see{" "}
          <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">Crisis Resources</Link>.
        </p>
      </div>
    </main>
  );
}
